import {z} from 'zod';
import {workflowWebflowMetadataSchema} from './agent';

/**
 * Schemas for the *source* form of an n8n workflow template — the
 * `metadata.json` + `workflow.json` pair an engineer authors under
 * `server/src/templates/<slug>/`. Codegen validates against these and merges
 * them into the served `templates.json`.
 *
 * These intentionally reuse the Webflow sidecar schema already defined in
 * `agent.ts` (`workflowWebflowMetadataSchema`) so there is a single source of
 * truth for the variable-type enum and customer-facing metadata, rather than a
 * second copy living in the infra repo.
 */

/**
 * n8n execution settings supported by a template. Add new fields here (as
 * `.optional()`) as n8n expands its settings schema.
 */
export const templateSettingsSchema = z
  .object({
    executionOrder: z.enum(['v1']),
    availableInMCP: z.boolean(),
    callerPolicy: z.enum(['workflowsFromSameOwner', 'any']).optional(),
    saveExecutionProgress: z.boolean().optional(),
  })
  .strict();
export type TemplateSettings = z.infer<typeof templateSettingsSchema>;

/**
 * Human-editable metadata for a template — the contents of
 * `templates/<slug>/metadata.json`.
 */
export const templateMetadataSchema = z
  .object({
    // Display name shown in the n8n UI.
    name: z.string().min(1),
    // Optional description of what the workflow does.
    description: z.string().optional(),
    // Whether the workflow is active when imported.
    active: z.boolean(),
    // n8n execution settings.
    settings: templateSettingsSchema,
    // Optional tags for filtering/organization in the n8n UI.
    tags: z.array(z.string()).default([]),
    // Environments this template is served in (e.g. acceptance, sai, prod).
    // Omit to serve the template in every environment. This is the infra-only
    // field publish.ts uses to scope a template per environment; it is stripped
    // before serving.
    environments: z.array(z.string()).optional(),
    // Webflow-specific sidecar (customer copy + user-editable variables).
    // Reused from agent.ts — single source of truth for the variable-type enum.
    webflow: workflowWebflowMetadataSchema.optional(),
  })
  .strict();
export type TemplateMetadata = z.infer<typeof templateMetadataSchema>;

/**
 * The n8n workflow definition exported from n8n — the contents of
 * `templates/<slug>/workflow.json`. Typically not hand-edited. Loose on
 * nodes/connections because the full n8n node typing is only needed when the
 * workflow is loaded into the canvas or pushed to the n8n API.
 */
export const workflowDefinitionSchema = z.object({
  id: z.string(),
  versionId: z.string(),
  meta: z
    .object({
      instanceId: z.string(),
    })
    .optional(),
  nodes: z.array(z.record(z.unknown())),
  connections: z.record(z.unknown()),
  pinData: z.record(z.unknown()).default({}),
});
export type WorkflowDefinition = z.infer<typeof workflowDefinitionSchema>;
